import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { MatButtonModule } from "@angular/material/button";
import { OverlayModule } from "@angular/cdk/overlay";
import {
  GroupedSelectGroup,
  filterGroupedSelectGroups,
  getGroupedSelectDisplayValue,
} from "src/app/utils/default-route/default-route.util";

@Component({
  selector: "app-grouped-search-select",
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    OverlayModule,
  ],
  templateUrl: "./grouped-search-select.component.html",
  styleUrls: ["./grouped-search-select.component.scss"],
})
export class GroupedSearchSelectComponent {
  private static readonly MIN_SEARCH_LENGTH = 3;

  @ViewChild("panelList") panelList?: ElementRef<HTMLElement>;

  @Input() label = "ruta-predeterminada";
  @Input() groups: GroupedSelectGroup[] = [];
  @Input() selectedValue: string | null = null;
  @Input() disabled = false;
  @Input() noneOptionLabel = "ninguna-ruta";
  @Input() searchPlaceholder = "buscar-rutas";
  @Input() compact = false;

  @Output() selectedValueChange = new EventEmitter<string | null>();

  isOpen = false;
  searchTerm = "";
  expandedGroupIds = new Set<string | number>();

  get displayValue(): string {
    if (!this.selectedValue) {
      return "";
    }
    return getGroupedSelectDisplayValue(this.groups, this.selectedValue);
  }

  get isSearchActive(): boolean {
    return (
      this.searchTerm.trim().length >=
      GroupedSearchSelectComponent.MIN_SEARCH_LENGTH
    );
  }

  get visibleGroups(): GroupedSelectGroup[] {
    if (this.isSearchActive) {
      return filterGroupedSelectGroups(this.groups, this.searchTerm);
    }
    return this.groups;
  }

  isGroupExpanded(groupId: string | number): boolean {
    if (this.isSearchActive) {
      return this.visibleGroups.some((group) => group.id === groupId);
    }
    return this.expandedGroupIds.has(groupId);
  }

  togglePanel(): void {
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.initializePanelState();
      setTimeout(() => this.scrollToSelectedItem(), 0);
    }
  }

  closePanel(): void {
    this.isOpen = false;
    this.searchTerm = "";
    this.expandedGroupIds.clear();
  }

  selectNone(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedValue = null;
    this.selectedValueChange.emit(null);
    this.closePanel();
  }

  selectItem(value: string, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedValue = value;
    this.selectedValueChange.emit(value);
    this.closePanel();
  }

  toggleGroup(groupId: string | number, event: MouseEvent): void {
    event.stopPropagation();

    if (this.isSearchActive) {
      return;
    }

    if (this.expandedGroupIds.has(groupId)) {
      this.expandedGroupIds.delete(groupId);
      return;
    }

    this.expandedGroupIds.add(groupId);
  }

  onSearchInput(event: Event): void {
    event.stopPropagation();
  }

  onSearchTermChange(): void {
    if (!this.isSearchActive) {
      this.initializePanelState(false);
    }
  }

  private initializePanelState(clearSearch = true): void {
    if (clearSearch) {
      this.searchTerm = "";
    }

    this.expandedGroupIds.clear();

    if (this.selectedValue) {
      const selectedGroup = this.groups.find((group) =>
        group.items.some((item) => item.value === this.selectedValue)
      );

      if (selectedGroup) {
        this.expandedGroupIds.add(selectedGroup.id);
      }
    }
  }

  private scrollToSelectedItem(): void {
    if (!this.panelList?.nativeElement || !this.selectedValue) {
      return;
    }

    setTimeout(() => {
      const selectedElement = this.panelList?.nativeElement?.querySelector(
        `[data-value="${this.selectedValue}"]`
      ) as HTMLElement | null;

      selectedElement?.scrollIntoView({ block: "nearest" });
    }, 50);
  }
}
